import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  MessageSquare,
  Users,
  Zap,
  Sparkles,
  CheckCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import Wrapper from "@/components/global/wrapper";
import Container from "@/components/global/container";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MagicCard } from "@/components/ui/magic-card";
import { OrbitingCircles } from "@/components/ui/orbiting-circles";
import Icons from "@/components/global/icons";
import { motion } from "framer-motion";
import { useState } from "react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import EmailDisplay from "@/components/global/EmailDisplay";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate form data
      if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.message.trim()) {
        toast.error("Please fill in all required fields");
        setIsSubmitting(false);
        return;
      }

      // Submit to backend API
      const response = await apiClient.submitContactForm({
        name: formData.name,
        email: formData.email,
        company: formData.company,
        subject: formData.subject,
        message: formData.message,
        website: "", // Honeypot field
      });

      if (response.error) {
        // Handle validation errors
        if (response.details) {
          const errorMessages = Object.entries(response.details)
            .map(([field, message]) => `${field}: ${message}`)
            .join(", ");
          toast.error(`Validation error: ${errorMessages}`);
        } else {
          toast.error(response.error);
        }
        setIsSubmitting(false);
        return;
      }

      setIsSubmitting(false);
      setSubmitted(true);
      toast.success(response.data?.message || "Message sent! We'll get back to you soon.");

      // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: "",
          email: "",
          company: "",
          subject: "",
          message: "",
        });
      }, 3000);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("An unexpected error occurred. Please try again.");
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Us",
      emailId: "support",
      description: "We'll get back to you within 24 hours",
    },
    {
      icon: Phone,
      title: "Call Us",
      details: "+41 4412345",
      description: "Mon-Fri 9AM-6PM PST",
    },
    {
      icon: MapPin,
      title: "Visit Us",
      details: "2 Elias-Canetti-Strasse, 8050 Zürich, Switzerland",
      description: "Schedule an in-person meeting",
    },
    {
      icon: Clock,
      title: "Support Hours",
      details: "24/7 Support",
      description: "We're always here to help",
    },
  ];

  const reasons = [
    {
      icon: MessageSquare,
      title: "General Inquiry",
      description: "Questions about our platform or services",
    },
    {
      icon: Users,
      title: "Sales & Demo",
      description: "Ready to see Blyrha in action?",
    },
    {
      icon: Zap,
      title: "Technical Support",
      description: "Need help with your account or features?",
    },
  ];

  return (
    <Wrapper className="py-20 relative" data-oid="bdj8jxm">
      {/* Enhanced Hero Section */}
      <div
        className="relative flex flex-col items-center justify-center w-full py-20"
        data-oid="5mx.ljm"
      >
        {/* Background blur effect */}
        <div
          className="absolute flex lg:hidden size-40 rounded-full bg-blue-500 blur-[10rem] top-0 left-1/2 -translate-x-1/2 -z-10"
          data-oid=".xdz22u"
        ></div>

        {/* Orbiting circles for desktop */}
        <Container
          className="hidden lg:flex absolute inset-0 top-0 mb-auto flex-col items-center justify-center w-full min-h-[60vh] -z-10"
          data-oid="9az08kq"
        >
          <OrbitingCircles speed={0.3} radius={180} data-oid="y0_0ns6">
            <Icons.circle1
              className="size-3 text-blue-500/60"
              data-oid="3vypulj"
            />
            <Icons.circle2
              className="size-1 text-purple-500/70"
              data-oid="4bw6i2a"
            />
          </OrbitingCircles>
          <OrbitingCircles speed={0.15} radius={280} data-oid="y62wol6">
            <Icons.circle2
              className="size-1 text-pink-500/50"
              data-oid=":_8a7l9"
            />
            <Icons.circle1
              className="size-4 text-blue-500/50"
              data-oid="pe:nrp0"
            />
          </OrbitingCircles>
        </Container>

        <Container data-oid="j6e3lux">
          <div
            className="text-center max-w-4xl mx-auto mb-16 relative z-10"
            data-oid="hhrdjif"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              data-oid="1b7s3cf"
            >
              <div
                className="group relative grid overflow-hidden rounded-full px-4 py-2 shadow-[0_1000px_0_0_hsl(0_0%_15%)_inset] transition-colors duration-200 mx-auto mb-6 w-fit"
                data-oid="lkefvuo"
              >
                <span data-oid="4226a19">
                  <span
                    className="spark mask-gradient absolute inset-0 h-[100%] w-[100%] animate-flip overflow-hidden rounded-full [mask:linear-gradient(white,_transparent_50%)] before:absolute before:aspect-square before:w-[200%] before:rotate-[-90deg] before:animate-rotate before:bg-[conic-gradient(from_0deg,transparent_0_340deg,white_360deg)] before:content-[''] before:[inset:0_auto_auto_50%] before:[translate:-50%_-15%]"
                    data-oid="oowe5_0"
                  />
                </span>
                <span
                  className="backdrop absolute inset-[1px] rounded-full bg-background transition-colors duration-200 group-hover:bg-neutral-800"
                  data-oid="un174mt"
                />
                <span
                  className="z-10 py-1 text-sm text-neutral-100 flex items-center"
                  data-oid="l36qrun"
                >
                  <MessageSquare
                    className="h-4 w-4 mr-2 text-blue-500"
                    data-oid="_j2g426"
                  />
                  Let's Connect
                </span>
              </div>
            </motion.div>
            <motion.h1
              className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 !leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-oid="65i4f0q"
            >
              Get in{" "}
              <span
                className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent"
                data-oid="x2avf8_"
              >
                Touch
              </span>
            </motion.h1>
            <motion.p
              className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              data-oid="057yju-"
            >
              Have questions about Blyrha? Want to see a demo? We'd love to hear
              from you. Our team is here to help you transform your marketing
              with AI.
            </motion.p>
          </div>
        </Container>
      </div>

      {/* Enhanced Contact Reasons */}
      <Container data-oid="asby9jz">
        <div className="grid md:grid-cols-3 gap-6 mb-16" data-oid="8pp.vz:">
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-oid="67z:01l"
              >
                <MagicCard
                  gradientFrom="#3b82f6"
                  gradientTo="#8b5cf6"
                  gradientColor="rgba(59,130,246,0.05)"
                  className="text-center p-6 rounded-2xl border bg-card/50 backdrop-blur-sm h-full"
                  data-oid="u8t7ifv"
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                    data-oid="3lgj1:v"
                  >
                    <Icon
                      className="h-12 w-12 text-blue-600 mx-auto mb-4"
                      data-oid="t2.lu3q"
                    />
                  </motion.div>
                  <h3 className="text-xl font-semibold mb-2" data-oid="2pm1kzm">
                    {reason.title}
                  </h3>
                  <p className="text-muted-foreground" data-oid="1jvdgr.">
                    {reason.description}
                  </p>
                </MagicCard>
              </motion.div>
            );
          })}
        </div>
      </Container>

      {/* Main Contact Section */}
      <Container data-oid="-6ccdx7">
        <div className="grid lg:grid-cols-2 gap-12 mb-16" data-oid="ytbv6jk">
          {/* Enhanced Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            data-oid="nzpg610"
          >
            <MagicCard
              gradientFrom="#3b82f6"
              gradientTo="#8b5cf6"
              gradientColor="rgba(59,130,246,0.05)"
              className="space-y-6 p-8 rounded-2xl border bg-card/50 backdrop-blur-sm"
              data-oid="_f86v4_"
            >
              <div data-oid="sw842nf">
                <h2 className="text-3xl font-bold mb-4" data-oid="h5m6otn">
                  Send us a message
                </h2>
                <p className="text-muted-foreground" data-oid="f63pkyb">
                  Fill out the form below and we'll get back to you as soon as
                  possible.
                </p>
              </div>

              {submitted ? (
                <motion.div
                  className="text-center p-8 rounded-2xl bg-green-50 border border-green-200"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  data-oid="9z:k23i"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    data-oid="z7mwsko"
                  >
                    <CheckCircle
                      className="h-16 w-16 text-green-600 mx-auto mb-4"
                      data-oid="2eflfy_"
                    />
                  </motion.div>
                  <h3
                    className="text-xl font-semibold text-green-800 mb-2"
                    data-oid="vpmnt53"
                  >
                    Message Sent!
                  </h3>
                  <p className="text-green-700" data-oid="bxlwh3n">
                    Thank you for reaching out. We'll get back to you within 24
                    hours.
                  </p>
                </motion.div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                  data-oid="e396bfa"
                >
                  <div className="grid md:grid-cols-2 gap-4" data-oid="snv2sn.">
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.1 }}
                      viewport={{ once: true }}
                      data-oid="u2:ldq4"
                    >
                      <label
                        className="block text-sm font-medium mb-2"
                        data-oid="-_94-6p"
                      >
                        Full Name *
                      </label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="John Doe"
                        className="w-full border-border/50 focus:border-blue-500 transition-colors"
                        data-oid="c-k8.bx"
                      />
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.2 }}
                      viewport={{ once: true }}
                      data-oid="stn91c2"
                    >
                      <label
                        className="block text-sm font-medium mb-2"
                        data-oid="16ak_3i"
                      >
                        Email *
                      </label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="john@company.com"
                        className="w-full border-border/50 focus:border-blue-500 transition-colors"
                        data-oid="ug0rvie"
                      />
                    </motion.div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4" data-oid="oa:exgx">
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.3 }}
                      viewport={{ once: true }}
                      data-oid="pkphk50"
                    >
                      <label
                        className="block text-sm font-medium mb-2"
                        data-oid="92i5b3i"
                      >
                        Company
                      </label>
                      <Input
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Your Company"
                        className="w-full border-border/50 focus:border-blue-500 transition-colors"
                        data-oid="0bh6sh2"
                      />
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.4 }}
                      viewport={{ once: true }}
                      data-oid="b01c-:f"
                    >
                      <label
                        className="block text-sm font-medium mb-2"
                        data-oid="ruwduzl"
                      >
                        Subject *
                      </label>
                      <Input
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        placeholder="How can we help?"
                        className="w-full border-border/50 focus:border-blue-500 transition-colors"
                        data-oid="qqxwi9x"
                      />
                    </motion.div>
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.5 }}
                    viewport={{ once: true }}
                    data-oid="j6nucup"
                  >
                    <label
                      className="block text-sm font-medium mb-2"
                      data-oid="dox4kfu"
                    >
                      Message *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      placeholder="Tell us about your project or ask us anything..."
                      rows={8}
                      className="w-full border-border/50 focus:border-blue-500 transition-colors min-h-[200px] md:min-h-[240px] resize-y"
                      data-oid="hi3wo68"
                    />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.6 }}
                    viewport={{ once: true }}
                    data-oid="rg_gd9-"
                  >
                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 shadow-lg shadow-blue-500/25"
                      data-oid=":emkzfr"
                    >
                      {isSubmitting ? (
                        <>
                          <div
                            className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"
                            data-oid="21t.lwd"
                          ></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message{" "}
                          <Send className="ml-2 h-4 w-4" data-oid="2sy7roe" />
                        </>
                      )}
                    </Button>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.7 }}
                    viewport={{ once: true }}
                    data-oid="faq-banner"
                  >
                    <MagicCard
                      gradientFrom="#3b82f6"
                      gradientTo="#8b5cf6"
                      gradientColor="rgba(59,130,246,0.06)"
                      className="mt-4 w-full rounded-2xl border border-border/50 bg-background/50 p-6"
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                            Need quick answers?
                          </p>
                          <h3 className="text-xl font-bold mt-1">Browse our FAQ library</h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            We've curated the most common questions from teams adopting Blyrha.
                          </p>
                        </div>
                        <Link to="/faq">
                          <Button
                            variant="outline"
                            className="w-full sm:w-auto border-border/60 hover:border-blue-500"
                          >
                            View FAQ
                          </Button>
                        </Link>
                      </div>
                    </MagicCard>
                  </motion.div>
                </form>
              )}
            </MagicCard>
          </motion.div>

          {/* Enhanced Contact Information */}
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            data-oid="wbpefac"
          >
            <div data-oid="f9.fl9k">
              <h2 className="text-3xl font-bold mb-4" data-oid="1716q:-">
                Contact Information
              </h2>
              <p className="text-muted-foreground" data-oid="3r_m_u7">
                Prefer to reach out directly? Here are all the ways to get in
                touch with us.
              </p>
            </div>

            <div className="space-y-4" data-oid="91gomu5">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                const displayContent = (info as any).emailId ? (
                  <EmailDisplay emailId={(info as any).emailId} fallback="support@blyrha.com" />
                ) : (
                  info.details
                );
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    data-oid="iipzb7-"
                  >
                    <MagicCard
                      gradientFrom="#3b82f6"
                      gradientTo="#8b5cf6"
                      gradientColor="rgba(59,130,246,0.03)"
                      className="flex items-start space-x-4 p-4 rounded-xl border bg-card/50 backdrop-blur-sm"
                      data-oid="6c66qs5"
                    >
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.2 }}
                        data-oid=":106hdg"
                      >
                        <Icon
                          className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0"
                          data-oid="3:qe-tc"
                        />
                      </motion.div>
                      <div data-oid="u7kay9b">
                        <h3 className="font-semibold mb-1" data-oid=":65:1e4">
                          {info.title}
                        </h3>
                        <p className="text-foreground mb-1" data-oid="ydnju-q">
                          {displayContent}
                        </p>
                        <p
                          className="text-sm text-muted-foreground"
                          data-oid="iin5dlw"
                        >
                          {info.description}
                        </p>
                      </div>
                    </MagicCard>
                  </motion.div>
                );
              })}
            </div>

            {/* Enhanced Quick Actions */}
            <div className="space-y-4 pt-6" data-oid="_0gbjys">
              <h3 className="text-xl font-semibold" data-oid="cqz6xru">
                Quick Actions
              </h3>
              <div className="space-y-3" data-oid="uzo8dms">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                  data-oid="8w70tug"
                >
                  <Link to="/demo" className="block" data-oid="hlfdt0h">
                    <Button
                      variant="outline"
                      className="w-full justify-start border-border/50 hover:bg-blue-50 hover:border-blue-200"
                      data-oid="i96zd54"
                    >
                      <Users className="mr-2 h-4 w-4" data-oid="2.-dh.x" />
                      Schedule a Demo
                    </Button>
                  </Link>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                  data-oid="-me__x4"
                >
                  <Link to="/demo" className="block" data-oid="171zmcm">
                    <Button
                      variant="outline"
                      className="w-full justify-start border-border/50 hover:bg-purple-50 hover:border-purple-200"
                      data-oid="u.e0dwj"
                    >
                      <Zap className="mr-2 h-4 w-4" data-oid="50o.-jf" />
                      Start Free Trial
                    </Button>
                  </Link>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </Container>

    </Wrapper>
  );
};

export default ContactPage;
